import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {UpcomingClassesV2} from "../../../../components/live-classes/upcoming-classes/v2";
import {CustomPagination} from "../../../../components/pagination";
import {DashboardWrapper} from "../../../../layouts/dashboard-wrapper";
import {StyledPageWrapper} from "../../../../styles/dashboard/calls/calls";
import {StyledPageHeader} from "../../../../styles/dashboard/calls/calls";
import {Alert, Button, Space, Spin, Tabs} from "antd";
import {PastLiveClassesV2} from "../../../../components/live-classes/past-classes/v2";
import {useLiveEvents} from "../../../../features/live-events";
import {FullPageLoader} from "../../../../components/full-page-loader";
import {LoadingSpinner} from "../../../../components/icons/icons";
import {StyledPageHeaderWrapper} from "../../../../components/calls/upcoming-calls/upcoming-calls.style";
import {TourWidget} from "../../../../components/widgets/tour-widget";
import {PlusOutlined, VideoCameraOutlined} from "@ant-design/icons";
import {CohostedClasses} from "../../../../components/live-classes/co-hosted-classes";
import {useGetCoHostedEvents} from "../../../../react-query-hooks/live-events/co-hosts/co-hosts";
import {
  StyledPublicProfileCountTag,
  StyledPublicProfileCountTagWrapper,
} from "../../../../styles/public-profile/public-profile";
import {StyledCreateButtonWrapper} from "../../../../layouts/dashboard-wrapper/dashboard-wrapper.style";
import {CanvaTemplateWidget} from "../../../../components/widgets/canva-template-widget";
import {ErrorBoundary} from "react-error-boundary";

const PER_PAGE = 10;

function LiveClassesPageV2() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const getLiveClassesQuery = useLiveEvents({
    filter: "upcoming",
    page,
    perPage: PER_PAGE,
  });
  const getCoHostedEvent = useGetCoHostedEvents("upcoming");

  const pageTitle = "Live events";

  if (getLiveClassesQuery.isLoading) {
    return (
      <DashboardWrapper pageTitle={pageTitle}>
        <StyledPageHeader>Live events</StyledPageHeader>
        <FullPageLoader />
      </DashboardWrapper>
    );
  }

  if (getLiveClassesQuery.isError) {
    return (
      <DashboardWrapper pageTitle={pageTitle}>
        <StyledPageHeader>Live events</StyledPageHeader>
        <Alert
          message="Error"
          description={getLiveClassesQuery.error.message}
          type="error"
          showIcon
        />
      </DashboardWrapper>
    );
  }

  const handleTabClick = (key: string) => {
    if (key === "2") {
      getCoHostedEvent.refetch();
    }
  };

  const flatMapCohostPages = getCoHostedEvent.data?.pages.flatMap(
    (page) => page?.data ?? []
  );
  const eventsData = getLiveClassesQuery.data?.data ?? [];

  const paginationMeta = getLiveClassesQuery.data
    ? {
        per_page: PER_PAGE,
        next_page: getLiveClassesQuery.data.hasNextPage ? page + 1 : 0,
        previous_page: getLiveClassesQuery.data.hasPrevPage ? page - 1 : 0,
        total_pages: Math.ceil(getLiveClassesQuery.data.total / PER_PAGE),
        current_page: page,
      }
    : undefined;

  return (
    <DashboardWrapper pageTitle={pageTitle}>
      <StyledPageWrapper>
        <div className="main-area" style={{maxWidth: "70rem"}}>
          <StyledPageHeaderWrapper>
            <StyledPageHeader>Live events</StyledPageHeader>
            {eventsData.length > 0 && (
              <StyledCreateButtonWrapper style={{display: "flex", justifyContent: "end"}}>
                <div>
                  <Button
                    onClick={() => navigate("/live-classes/new")}
                    type="primary"
                    size="small"
                  >
                    <span className="show-desktop">Create new live event</span>
                    <span className="show-mobile">
                      <PlusOutlined style={{fontSize: "2.4rem"}} />
                    </span>
                  </Button>
                </div>
              </StyledCreateButtonWrapper>
            )}
          </StyledPageHeaderWrapper>
          <Tabs defaultActiveKey="1" onTabClick={(key) => handleTabClick(key)}>
            <Tabs.TabPane tab="Upcoming events" key="1">
              <ErrorBoundary
                FallbackComponent={({error, resetErrorBoundary}) => (
                  <div style={{marginTop: "1.8rem"}}>
                    <Alert
                      message="Sorry, something went wrong."
                      showIcon
                      description={error.message}
                      type="error"
                      action={
                        <Button
                          onClick={() => {
                            resetErrorBoundary();
                          }}
                          size="small"
                          danger
                        >
                          Reset
                        </Button>
                      }
                    />
                  </div>
                )}
              >
                <Spin
                  spinning={getLiveClassesQuery.isFetching}
                  tip="Updating..."
                  indicator={LoadingSpinner}
                >
                  <UpcomingClassesV2 data={eventsData} />
                  {eventsData.length > 0 && (
                    <CustomPagination
                      page={page}
                      setPage={setPage}
                      isPreviousData={getLiveClassesQuery.isPreviousData}
                      paginationMeta={paginationMeta}
                    />
                  )}
                </Spin>
              </ErrorBoundary>
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={
                <StyledPublicProfileCountTagWrapper>
                  Co-hosted events{" "}
                  {flatMapCohostPages && flatMapCohostPages.length > 0 && (
                    <StyledPublicProfileCountTag>
                      {flatMapCohostPages.length}
                    </StyledPublicProfileCountTag>
                  )}
                </StyledPublicProfileCountTagWrapper>
              }
              key="2"
            >
              <ErrorBoundary
                FallbackComponent={({error, resetErrorBoundary}) => (
                  <div style={{marginTop: "1.8rem"}}>
                    <Alert
                      message="Sorry, something went wrong."
                      showIcon
                      description={error.message}
                      type="error"
                      action={
                        <Button
                          onClick={() => {
                            resetErrorBoundary();
                          }}
                          size="small"
                          danger
                        >
                          Reset
                        </Button>
                      }
                    />
                  </div>
                )}
              >
                <CohostedClasses />
              </ErrorBoundary>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Past events" key="3">
              <ErrorBoundary
                FallbackComponent={({error, resetErrorBoundary}) => (
                  <div style={{marginTop: "1.8rem"}}>
                    <Alert
                      message="Sorry, something went wrong."
                      showIcon
                      description={error.message}
                      type="error"
                      action={
                        <Button
                          onClick={() => {
                            resetErrorBoundary();
                          }}
                          size="small"
                          danger
                        >
                          Reset
                        </Button>
                      }
                    />
                  </div>
                )}
              >
                <PastLiveClassesV2 />
              </ErrorBoundary>
            </Tabs.TabPane>
          </Tabs>
        </div>
        <div className="widget-area">
          <Space direction="vertical" size={32} style={{display: "flex"}}>
            <TourWidget
              buttons={[
                <Button
                  href="https://www.loom.com/share/42b9e6a7bedd41adabdf7f2e3e867613?sid=5be5070b-91c2-4b74-b7d9-4b75ace46420"
                  block
                  icon={<VideoCameraOutlined />}
                  size="small"
                  target="_blank"
                  key="watch-demo"
                >
                  <span>Watch demo video</span>
                </Button>,
              ]}
            />
            <CanvaTemplateWidget url="https://www.canva.com/design/DAGlpIdhnIM/F8Cm2J4qs5rEynagIBJzJg/view?utm_content=DAGlpIdhnIM&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview" />
          </Space>
        </div>
      </StyledPageWrapper>
    </DashboardWrapper>
  );
}

export {LiveClassesPageV2};
